/**
 * Uploads one screenshot or screen recording to the feedback attachment endpoint.
 *
 * Binary does not go through GraphQL — base64 would inflate the payload by about
 * a third and land in the Apollo cache. This posts multipart to
 * /api/feedback/attachments and returns the attachment id, which the create
 * mutation then claims.
 *
 * The two media types take different paths:
 *
 * - **Images** are downscaled and re-encoded to WebP here first. A 2 MB PNG
 *   screenshot becomes roughly 150 kB with no loss of legibility, which matters
 *   most on a site connection.
 * - **Video** cannot be re-encoded in the browser (that would mean ffmpeg.wasm),
 *   so it uploads at original size against a tighter cap. What we *do* produce
 *   here is the **poster frame**: the file is loaded into a detached <video>,
 *   seeked about a second in, and drawn to a canvas. That saves putting ffmpeg
 *   on the server purely for thumbnails, and gives us the duration for free —
 *   which lets a too-long clip be refused *before* a 25 MB upload starts.
 */

import { useCallback, useState } from "react";
import { toast } from "sonner";

import { getAuthToken } from "@/lib/getAuthToken";
import type { FeedbackMediaType } from "@/types";

/** Longest edge kept when downscaling an image. Enough to read UI text. */
const MAX_EDGE = 2000;
const WEBP_QUALITY = 0.85;
const IMAGE_MAX_BYTES = 10 * 1024 * 1024;

/** Must match settings.feedback_video_max_bytes / _max_seconds on the backend. */
const VIDEO_MAX_BYTES = 25 * 1024 * 1024;
const VIDEO_MAX_SECONDS = 120;

/** Longest edge of the generated poster frame. Matches feedback_thumb_max_px. */
const POSTER_MAX_EDGE = 320;
const POSTER_QUALITY = 0.8;

export interface UploadedAttachment {
  id: string;
  mediaType: FeedbackMediaType;
  filename: string;
  sizeBytes: number;
  width: number;
  height: number;
  /** VIDEO only; null for images. */
  durationSeconds: number | null;
  /**
   * Local object URL for the tray preview — never sent anywhere. Null when it is
   * a video whose poster frame could not be produced, in which case the tray
   * shows a placeholder.
   */
  previewUrl: string | null;
  /** kuvateksti, filled in by the user in the tray. */
  caption: string;
}

interface VideoProbe {
  poster: Blob | null;
  width: number;
  height: number;
  durationSeconds: number;
}

async function downscale(file: File): Promise<Blob> {
  const bitmap = await createImageBitmap(file);
  const scale = Math.min(1, MAX_EDGE / Math.max(bitmap.width, bitmap.height));
  const width = Math.round(bitmap.width * scale);
  const height = Math.round(bitmap.height * scale);

  const canvas = document.createElement("canvas");
  canvas.width = width;
  canvas.height = height;
  const ctx = canvas.getContext("2d");
  if (!ctx) return file;
  ctx.drawImage(bitmap, 0, 0, width, height);
  bitmap.close();

  const blob = await new Promise<Blob | null>((resolve) =>
    canvas.toBlob(resolve, "image/webp", WEBP_QUALITY),
  );
  // If the browser cannot produce WebP, send the original — the server
  // re-encodes anyway, so this is a size optimisation, not a requirement.
  return blob ?? file;
}

/**
 * Read a video's dimensions and duration, and grab a poster frame.
 *
 * Everything here is best-effort. A codec the browser cannot decode (HEVC out of
 * a phone, say) resolves with no poster and zeroed metadata rather than
 * throwing: the clip is still perfectly uploadable and playable elsewhere, so
 * refusing it over a missing thumbnail would be the wrong trade.
 */
async function probeVideo(file: File): Promise<VideoProbe> {
  const empty: VideoProbe = {
    poster: null,
    width: 0,
    height: 0,
    durationSeconds: 0,
  };
  const video = document.createElement("video");
  video.preload = "metadata";
  video.muted = true;
  const src = URL.createObjectURL(file);
  video.src = src;

  try {
    await new Promise<void>((resolve, reject) => {
      video.onloadeddata = () => resolve();
      video.onerror = () => reject(new Error("undecodable"));
      // Don't hang the dialog on a file the browser silently refuses to open.
      window.setTimeout(() => reject(new Error("timeout")), 10_000);
    });

    const duration = Number.isFinite(video.duration) ? video.duration : 0;

    // A frame a second in is usually more representative than frame zero, which
    // is often a blank window or a fade. Skip the seek when the clip is too
    // short for it to matter — seeking to the current position never fires
    // `seeked`, which would deadlock the promise below.
    const target = Math.min(1, duration / 2);
    if (target > 0.05) {
      await new Promise<void>((resolve) => {
        video.onseeked = () => resolve();
        window.setTimeout(resolve, 3000);
        video.currentTime = target;
      });
    }

    const frameWidth = video.videoWidth;
    const frameHeight = video.videoHeight;
    if (!frameWidth || !frameHeight) {
      return { ...empty, durationSeconds: Math.round(duration) };
    }

    const scale = Math.min(1, POSTER_MAX_EDGE / Math.max(frameWidth, frameHeight));
    const canvas = document.createElement("canvas");
    canvas.width = Math.round(frameWidth * scale);
    canvas.height = Math.round(frameHeight * scale);
    const ctx = canvas.getContext("2d");
    let poster: Blob | null = null;
    if (ctx) {
      ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
      poster = await new Promise<Blob | null>((resolve) =>
        canvas.toBlob(resolve, "image/webp", POSTER_QUALITY),
      );
    }

    return {
      poster,
      width: frameWidth,
      height: frameHeight,
      durationSeconds: Math.round(duration),
    };
  } catch {
    return empty;
  } finally {
    URL.revokeObjectURL(src);
    video.removeAttribute("src");
  }
}

export function useFeedbackAttachmentUpload() {
  const [uploading, setUploading] = useState(false);

  const upload = useCallback(
    async (file: File): Promise<UploadedAttachment | null> => {
      const isVideo = file.type.startsWith("video/");
      const isImage = file.type.startsWith("image/");
      if (!isVideo && !isImage) {
        toast.error("Vain kuva- ja videotiedostot kelpaavat");
        return null;
      }

      // Client-side caps exist to fail fast and say something useful. The server
      // enforces them again from the real bytes — the MIME type here comes from
      // the OS and is only a hint.
      if (isImage && file.size > IMAGE_MAX_BYTES) {
        toast.error("Kuva on liian suuri. Enimmäiskoko on 10 MB.");
        return null;
      }
      if (isVideo && file.size > VIDEO_MAX_BYTES) {
        const actual = Math.round(file.size / (1024 * 1024));
        toast.error(
          `Video on liian suuri (${actual} MB). Enimmäiskoko on 25 MB — ` +
            "kokeile lyhyempää pätkää tai Windowsin leikkaustyökalun tallennusta.",
        );
        return null;
      }

      setUploading(true);
      try {
        const form = new FormData();
        let previewSource: Blob | null = null;

        if (isVideo) {
          const probe = await probeVideo(file);
          if (probe.durationSeconds > VIDEO_MAX_SECONDS) {
            toast.error(
              `Video on liian pitkä (${Math.round(probe.durationSeconds)} s). ` +
                `Enimmäispituus on ${VIDEO_MAX_SECONDS / 60} min.`,
            );
            return null;
          }
          form.append("file", file, file.name || "kuvakaappaus.mp4");
          if (probe.poster) {
            form.append("poster", probe.poster, "poster.webp");
            previewSource = probe.poster;
          }
          // Dimensions and duration cannot be read server-side without parsing
          // containers, so the browser reports them. Display metadata only.
          form.append("width", String(probe.width));
          form.append("height", String(probe.height));
          form.append("duration_seconds", String(probe.durationSeconds));
        } else {
          const compressed = await downscale(file);
          form.append("file", compressed, file.name || "kuvakaappaus.png");
          previewSource = compressed;
        }

        const token = await getAuthToken();
        const response = await fetch("/api/feedback/attachments", {
          method: "POST",
          headers: token ? { Authorization: `Bearer ${token}` } : undefined,
          body: form,
        });

        if (!response.ok) {
          // Read the body once — json() and text() cannot both consume it.
          const body = await response.text().catch(() => "");
          let detail: string | null = null;
          try {
            // The server's message names the real limit, so prefer it over ours.
            detail = JSON.parse(body)?.detail ?? null;
          } catch {
            // Not JSON, which means the API never saw this request: a dev proxy
            // or gateway answered instead. Log it — a bare Finnish toast sends
            // whoever hits this looking for a bug in the upload itself.
            console.error(
              "Attachment upload failed",
              response.status,
              body.slice(0, 500),
            );
          }
          // The status stays in the message: without it, a proxy failure and a
          // rejected file are indistinguishable to whoever reports the problem.
          toast.error(
            detail ?? `Liitteen lähetys epäonnistui (${response.status})`,
          );
          return null;
        }

        const data = await response.json();
        return {
          id: data.id,
          mediaType: data.mediaType,
          filename: data.filename,
          sizeBytes: data.sizeBytes,
          width: data.width,
          height: data.height,
          durationSeconds: data.durationSeconds ?? null,
          previewUrl: previewSource ? URL.createObjectURL(previewSource) : null,
          caption: "",
        };
      } catch {
        toast.error("Liitteen lähetys epäonnistui");
        return null;
      } finally {
        setUploading(false);
      }
    },
    [],
  );

  return { uploading, upload };
}
